import React, {
    useEffect,
    useRef,
    useState,
} from "react";

import { Link } from "react-router-dom";
import { useLanguage } from "../contexts/LanguageContext";
import {
    fetchDirectorMovieRoleFromWikipedia
} from "../utils/wikipediaDirectorMovieRole";


// ======================================================
// MOVIEFRENZY — RÉALISATION DE CE FILM
// Prototype réalisateur
// ======================================================

const DIRECTOR_MOVIE_COPY = {
    fr: {
        trigger: "Réalisation de ce film",
        title: "Réalisation de ce film",
        movie: "Film",
        function: "Fonction",
        work: "Travail de réalisation",
        development: "Développement du film",
        context: "Dans ce film",
        close: "Fermer la réalisation de ce film",
    },

    en: {
        trigger: "Directing this film",
        title: "Directing this film",
        movie: "Film",
        function: "Function",
        work: "Directorial work",
        development: "Film development",
        context: "In this film",
        close: "Close directing information",
    },

    es: {
        trigger: "Dirección de esta película",
        title: "Dirección de esta película",
        movie: "Película",
        function: "Función",
        work: "Trabajo de dirección",
        development: "Desarrollo de la película",
        context: "En esta película",
        close: "Cerrar información de dirección",
    },

    de: {
        trigger: "Regie bei diesem Film",
        title: "Regie bei diesem Film",
        movie: "Film",
        function: "Funktion",
        work: "Regiearbeit",
        development: "Filmentwicklung",
        context: "In diesem Film",
        close: "Regieinformationen schließen",
    },
};


// ======================================================
// GARDE-FOU — TRAVAIL DE RÉALISATION / CONTEXTE
// Évite qu'un synopsis Wikipédia complet soit recyclé
// comme description du travail du réalisateur.
// ======================================================

const normalizeComparableText = (text = "") =>
    String(text)
        .toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/[^\p{L}\p{N}\s]/gu, " ")
        .replace(/\s+/g, " ")
        .trim();

const cleanWikipediaText = (text = "") =>
    String(text)
        // caractères invisibles parfois présents entre les références Wiki
        .replace(/[\u200B-\u200D\uFEFF]/g, "")

        // références Wikipédia :
        // [1] [2] [17] [1-3] [1, 2]
        .replace(
            /\[\s*\d+(?:\s*[,;–-]\s*\d+)*\s*\]/g,
            ""
        )

        // évite "texte ." après suppression d'une référence
        .replace(/\s+([,.;:!?])/g, "$1")

        // espaces multiples
        .replace(/\s{2,}/g, " ")

        .trim();

const cleanExtractedSentence = (text = "") => {
    const cleanedText =
        cleanWikipediaText(text);

    if (!cleanedText) {
        return "";
    }

    return cleanedText.replace(
        /\s*:\s*$/,
        "."
    );
};

const areTextsTooSimilar = (textA, textB) => {
    const a = normalizeComparableText(textA);
    const b = normalizeComparableText(textB);

    if (!a || !b) {
        return false;
    }

    if (a === b) {
        return true;
    }

    if (
        a.length >= 20 &&
        b.length >= 20 &&
        (a.includes(b) || b.includes(a))
    ) {
        return true;
    }

    const wordsA = new Set(a.split(" "));
    const wordsB = new Set(b.split(" "));

    const commonWords =
        [...wordsA].filter((word) =>
            wordsB.has(word)
        ).length;

    const smallestSetSize =
        Math.min(wordsA.size, wordsB.size);

    if (!smallestSetSize) {
        return false;
    }

    return (
        commonWords / smallestSetSize
    ) >= 0.8;
};

// ======================================================
// GARDE-FOU — CONTEXTE "DANS CE FILM"
// Évite d'afficher un synopsis / résumé Wikipédia général
// à la place d'un contexte pertinent sur le réalisateur.
// ======================================================

const isGenericMovieSynopsis = ({
    text,
    movieTitle,
}) => {
    const normalizedText =
        normalizeComparableText(text);

    const normalizedMovieTitle =
        normalizeComparableText(movieTitle);

    if (!normalizedText) {
        return false;
    }

    // Texte beaucoup trop long pour ce bloc.
    if (normalizedText.length > 420) {
        return true;
    }

    // Exemples typiques :
    // "Dune est un film..."
    // "Dune is a film..."
    // "Dune es una película..."
    // "Dune ist ein Film..."
    const genericMoviePatterns = [
        "est un film",
        "est une film",
        "est un thriller",

        "is a film",
        "is an american film",
        "is a thriller",

        "es una pelicula",
        "es un film",
        "es un thriller",

        "ist ein film",
        "ist ein amerikanischer film",
        "ist ein kriminalfilm",
        "ist ein thriller",
    ];

    if (
        normalizedMovieTitle &&
        normalizedText.startsWith(
            normalizedMovieTitle
        ) &&
        genericMoviePatterns.some(
            (pattern) =>
                normalizedText.includes(pattern)
        )
    ) {
        return true;
    }

    return false;
};

// ======================================================
// SAUVETAGE INTELLIGENT — CONTEXTE RÉALISATEUR
//
// Quand Wikipédia renvoie un gros paragraphe générique,
// on tente d'en extraire UNE phrase réellement utile
// pour comprendre le contexte cinématographique du film.
//
// Exemple Dune :
// "C'est la troisième adaptation du roman..."
// au lieu d'afficher tout le synopsis Wikipédia.
// ======================================================

const extractUsefulDirectorContext = ({
    text,
    movieTitle,
    directorName,
    language = "fr",
}) => {
    if (!text) {
        return null;
    }

    const normalizedMovieTitle =
        normalizeComparableText(movieTitle);

    const normalizedDirectorName =
        normalizeComparableText(directorName);

    const languageKey =
        String(language || "fr")
            .split("-")[0];


    // --------------------------------------------------
    // Termes qui indiquent un contexte cinématographique
    // intéressant autour du film.
    // --------------------------------------------------

    const contextTerms = {
        fr: [
            "adaptation",
            "adapté de",
            "adaptée de",
            "roman",
            "œuvre",
            "inspiré de",
            "inspirée de",
            "remake",
            "suite",
            "préquelle",
            "reboot",
            "nouvelle version",
            "version cinématographique",
            "troisième adaptation",
            "deuxième adaptation",
            "première adaptation",
        ],

        en: [
            "adaptation",
            "adapted from",
            "novel",
            "book",
            "work by",
            "based on",
            "inspired by",
            "remake",
            "sequel",
            "prequel",
            "reboot",
            "film version",
            "third adaptation",
            "second adaptation",
            "first adaptation",
        ],

        es: [
            "adaptación",
            "adaptada de",
            "adaptado de",
            "novela",
            "obra",
            "basada en",
            "basado en",
            "inspirada en",
            "inspirado en",
            "remake",
            "secuela",
            "precuela",
            "reinicio",
            "versión cinematográfica",
        ],

        de: [
            "adaption",
            "adaptation",
            "verfilmung",
            "roman",
            "werk",
            "basiert auf",
            "inspiriert von",
            "neuverfilmung",
            "fortsetzung",
            "prequel",
            "reboot",
            "filmversion",
        ],
    }[languageKey] || [];


    // --------------------------------------------------
    // Signaux typiques d'un résumé d'intrigue.
    // On ne veut PAS récupérer ce genre de phrase.
    // --------------------------------------------------

    const plotTerms = {
        fr: [
            "l'histoire suit",
            "le film suit",
            "raconte l'histoire",
            "raconte",
            "le personnage",
            "le protagoniste",
            "doit affronter",
            "tente de",
        ],

        en: [
            "the story follows",
            "the film follows",
            "follows the story",
            "tells the story",
            "the protagonist",
            "the character",
            "must face",
            "tries to",
        ],

        es: [
            "la historia sigue",
            "la película sigue",
            "cuenta la historia",
            "el protagonista",
            "el personaje",
            "debe enfrentarse",
            "intenta",
        ],

        de: [
            "die handlung folgt",
            "der film folgt",
            "erzählt die geschichte",
            "die hauptfigur",
            "der protagonist",
            "muss sich",
            "versucht",
        ],
    }[languageKey] || [];


    // --------------------------------------------------
    // Phrase introductive générique Wikipédia.
    // --------------------------------------------------

    const genericMoviePatterns = [
        "est un film",
        "est une film",
        "est un thriller",

        "is a film",
        "is an american film",
        "is a thriller",

        "es una pelicula",
        "es un film",
        "es un thriller",

        "ist ein film",
        "ist ein amerikanischer film",
        "ist ein kriminalfilm",
        "ist ein thriller",
    ];


    const sentences =
        String(text).match(
            /[^.!?…]+(?:[.!?…]+|$)/g
        ) || [String(text)];


    const candidates = [];


    sentences.forEach((rawSentence, index) => {

        const sentence =
            String(rawSentence)
                .replace(/\s+/g, " ")
                .trim();

        if (!sentence) {
            return;
        }


        const normalizedSentence =
            normalizeComparableText(sentence);


        // Trop petit = information peu exploitable.
        if (sentence.length < 45) {
            return;
        }


        // On accepte ici un peu plus long que pour
        // "Travail de réalisation", car une phrase
        // de contexte encyclopédique peut être riche.
        if (sentence.length > 380) {
            return;
        }


        const contextHits =
            contextTerms.filter((term) =>
                normalizedSentence.includes(
                    normalizeComparableText(term)
                )
            );


        if (!contextHits.length) {
            return;
        }


        const containsPlotSignal =
            plotTerms.some((term) =>
                normalizedSentence.includes(
                    normalizeComparableText(term)
                )
            );


        if (containsPlotSignal) {
            return;
        }


        const isGenericLead =
            genericMoviePatterns.some((pattern) =>
                normalizedSentence.includes(
                    normalizeComparableText(pattern)
                )
            ) &&
            (
                !normalizedMovieTitle ||
                normalizedSentence.startsWith(
                    normalizedMovieTitle
                )
            );


        if (isGenericLead) {
            return;
        }


        let score =
            contextHits.length * 5;


        // Bonus si la phrase parle aussi explicitement
        // du réalisateur.
        if (
            normalizedDirectorName &&
            normalizedSentence.includes(
                normalizedDirectorName
            )
        ) {
            score += 3;
        }


        // On préfère les phrases compactes.
        if (sentence.length <= 260) {
            score += 2;
        }


        // Léger bonus aux premières phrases :
        // Wikipédia place souvent le contexte essentiel
        // au début de l'article.
        score +=
            Math.max(0, 3 - index);


        candidates.push({
            sentence,
            score,
        });
    });


    if (!candidates.length) {
        return null;
    }


    candidates.sort(
        (candidateA, candidateB) =>
            candidateB.score -
            candidateA.score
    );


    return candidates[0].sentence;
};

const DirectorMovieRolePopover = ({
    directorId,
    directorName,
    movie,
}) => {
    const { currentLanguage } = useLanguage();

    const popoverRef = useRef(null);

    const [
        isPopoverOpen,
        setIsPopoverOpen
    ] = useState(false);

    const [
        wikipediaDirectorData,
        setWikipediaDirectorData
    ] = useState(null);


    const languageKey =
        String(currentLanguage || "en")
            .split("-")[0];

    const copy =
        DIRECTOR_MOVIE_COPY[languageKey] ||
        DIRECTOR_MOVIE_COPY.en;

    const movieId = movie?.id;
    const movieTitle = movie?.title;
    const movieOriginalTitle = movie?.original_title;
    const movieReleaseDate = movie?.release_date;

    useEffect(() => {

        // On vide le résultat précédent quand
        // le réalisateur, le film ou la langue change.
        setWikipediaDirectorData(null);

        // ====================================================
        // MODE GÉNÉRALISÉ
        // Toute combinaison réalisateur + film peut maintenant
        // interroger Wikipédia.
        // ====================================================

        if (
            !directorId ||
            !directorName ||
            !movieId
        ) {
            return undefined;
        }
        let cancelled = false;

        const fetchWikipediaDirector = async () => {

            try {

                const result =
                    await fetchDirectorMovieRoleFromWikipedia({
                        movie: {
                            id: movieId,
                            title: movieTitle,
                            original_title: movieOriginalTitle,
                            release_date: movieReleaseDate,
                        },
                        directorName,
                        language: languageKey,
                    });


                if (!cancelled) {

                    console.log(
                        "🎬 MovieFrenzy Wikipedia director:",
                        result
                    );

                    setWikipediaDirectorData(
                        result
                    );
                }

            } catch (error) {

                if (!cancelled) {

                    console.error(
                        "❌ Wikipedia director error:",
                        error
                    );

                    setWikipediaDirectorData(
                        null
                    );
                }
            }
        };


        fetchWikipediaDirector();


        return () => {
            cancelled = true;
        };

    }, [
        directorId,
        directorName,
        movieId,
        movieTitle,
        movieOriginalTitle,
        movieReleaseDate,
        languageKey,
    ]);


    const popoverId =
        `director-movie-role-${directorId || "person"}-${movie?.id || "movie"}`;


    useEffect(() => {
        const popover = popoverRef.current;

        if (!popover) {
            return undefined;
        }

        const handleToggle = (event) => {
            setIsPopoverOpen(
                event.newState === "open"
            );
        };

        popover.addEventListener(
            "toggle",
            handleToggle
        );

        return () => {
            popover.removeEventListener(
                "toggle",
                handleToggle
            );
        };
    }, []);


    useEffect(() => {
        setIsPopoverOpen(false);
    }, [movie?.id]);


    const openPopover = () => {
        const popover = popoverRef.current;

        if (
            popover &&
            typeof popover.showPopover === "function"
        ) {
            popover.showPopover();
        }
    };


    const closePopover = () => {
        const popover = popoverRef.current;

        if (
            popover &&
            typeof popover.hidePopover === "function"
        ) {
            popover.hidePopover();
        }
    };


    // Aucun affichage si nous n'avons ni film,
    // ni données Wikipédia valides, ni données démo.
    if (
        !movie ||
        !directorId ||
        !directorName
    ) {
        return null;
    }


    const wikipediaDirectorFunction = {
        fr: "Réalisateur",
        en: "Director",
        es: "Director",
        de: "Regisseur",
    };

    const directorFunction =
        wikipediaDirectorFunction[languageKey] ||
        wikipediaDirectorFunction.en;


    const wikipediaDirectorWorkIsTooLong =
        (
            wikipediaDirectorData?.found === true &&
            wikipediaDirectorData?.directorWorkSentence
        )
            ? wikipediaDirectorData.directorWorkSentence.length > 260
            : false;


    const wikipediaDirectorWorkAndContextAreTooSimilar =
        (
            wikipediaDirectorData?.found === true &&
            wikipediaDirectorData?.directorWorkSentence &&
            wikipediaDirectorData?.directorSentence
        )
            ? areTextsTooSimilar(
                wikipediaDirectorData.directorWorkSentence,
                wikipediaDirectorData.directorSentence
            )
            : false;


    const work =
        wikipediaDirectorData?.directorWorkSentence &&
            !wikipediaDirectorWorkIsTooLong &&
            !wikipediaDirectorWorkAndContextAreTooSimilar
            ? cleanExtractedSentence(
                wikipediaDirectorData.directorWorkSentence
            )
            : null;

    const directorWorkType =
        wikipediaDirectorData?.found === true
            ? wikipediaDirectorData?.directorWorkType || null
            : null;

    const workLabel =
        directorWorkType === "development"
            ? copy.development
            : copy.work;

    const wikipediaDirectorContextIsGeneric =
        (
            wikipediaDirectorData?.found === true &&
            wikipediaDirectorData?.directorSentence
        )
            ? isGenericMovieSynopsis({
                text:
                    wikipediaDirectorData
                        .directorSentence,

                movieTitle:
                    movie?.title ||
                    movie?.original_title ||
                    "",
            })
            : false;


    // ======================================================
    // Si Wikipédia nous a donné un gros synopsis générique,
    // on tente maintenant d'en sauver UNE phrase utile.
    // ======================================================

    const rescuedWikipediaContext =
        (
            wikipediaDirectorData?.found === true &&
            wikipediaDirectorData?.directorSentence &&
            wikipediaDirectorContextIsGeneric
        )
            ? extractUsefulDirectorContext({
                text:
                    wikipediaDirectorData
                        .directorSentence,

                movieTitle:
                    movie?.title ||
                    movie?.original_title ||
                    "",

                directorName,

                language:
                    wikipediaDirectorData?.language ||
                    languageKey,
            })
            : null;


    // ======================================================
    // CONTEXTE FINAL
    //
    // 1. phrase Wikipédia normale si elle est pertinente
    // 2. sinon phrase sauvée intelligemment
    // 3. sinon null
    // ======================================================

    const rawContext =
        wikipediaDirectorData?.found === true
            ? (
                wikipediaDirectorData?.directorSentence &&
                    !wikipediaDirectorContextIsGeneric
                    ? wikipediaDirectorData.directorSentence
                    : rescuedWikipediaContext
            )
            : null;

    const context =
        rawContext
            ? cleanExtractedSentence(rawContext)
            : null;

    const movieYear =
        movie.release_date
            ? movie.release_date.slice(0, 4)
            : null;

    const moviePosterUrl =
        movie?.poster_path
            ? `https://image.tmdb.org/t/p/w185${movie.poster_path}`
            : null;


    return (
        <>
            <button
                type="button"
                className="actor-movie-role-trigger"
                aria-controls={popoverId}
                aria-expanded={isPopoverOpen}
                aria-haspopup="dialog"
                onClick={openPopover}
            >
                <span
                    className="actor-movie-role-trigger__icon"
                    aria-hidden="true"
                >
                    🎬
                </span>

                <span className="actor-movie-role-trigger__label">
                    {copy.trigger}
                </span>
            </button>


            <div
                ref={popoverRef}
                id={popoverId}
                className="actor-movie-role-popover"
                popover="auto"
                role="dialog"
                aria-labelledby={`${popoverId}-title`}
                data-wikipedia-director-found={
                    wikipediaDirectorData?.found === true
                        ? "true"
                        : "false"
                }
            >
                <header className="actor-movie-role-popover__header">

                    <div className="actor-movie-role-popover__heading">

                        <span className="actor-movie-role-popover__eyebrow">
                            🎬 MovieFrenzy
                        </span>

                        <h2 id={`${popoverId}-title`}>
                            {copy.title}
                        </h2>

                        <p className="actor-movie-role-popover__actor">
                            {directorName}
                        </p>

                    </div>


                    <div className="actor-movie-role-popover__actions">

                        <button
                            type="button"
                            className="actor-movie-role-popover__close"
                            onClick={closePopover}
                            aria-label={copy.close}
                        >
                            ×
                        </button>

                        {moviePosterUrl && (
                            <Link
                                to={`/ficheFilm/${movie.id}`}
                                className="actor-movie-role-popover__poster-link"
                                onClick={closePopover}
                                aria-label={`${copy.movie} : ${movie.title}`}
                                title={movie.title}
                            >
                                <img
                                    src={moviePosterUrl}
                                    alt=""
                                    className="actor-movie-role-popover__poster"
                                />
                            </Link>
                        )}

                    </div>

                </header>


                <Link
                    to={`/ficheFilm/${movie.id}`}
                    className="actor-movie-role-film"
                    onClick={closePopover}
                >
                    <span
                        className="actor-movie-role-film__icon"
                        aria-hidden="true"
                    >
                        🎬
                    </span>

                    <div>
                        <small>
                            {copy.movie}
                        </small>

                        <strong>
                            {movie.title}
                            {movieYear && ` — ${movieYear}`}
                        </strong>
                    </div>
                </Link>


                <section className="actor-movie-role-section">

                    <h3>
                        <span aria-hidden="true">
                            🎥
                        </span>

                        {copy.function}
                    </h3>

                    <p className="actor-movie-role-character">
                        {directorFunction}
                    </p>

                    {wikipediaDirectorData?.found === true &&
                        wikipediaDirectorData?.sourceUrl && (
                            <a
                                href={wikipediaDirectorData.sourceUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="actor-movie-role-wikipedia-source"
                            >
                                Wikipédia ↗
                            </a>
                        )}

                </section>


                {work && (
                    <section className="actor-movie-role-section">

                        <h3>
                            <span aria-hidden="true">
                                🎯
                            </span>

                            {workLabel}
                        </h3>

                        <p>
                            {work}
                        </p>

                    </section>
                )}


                {context && (
                    <section className="actor-movie-role-section">

                        <h3>
                            <span aria-hidden="true">
                                📖
                            </span>

                            {copy.context}
                        </h3>

                        <p className="actor-movie-role-story">
                            {context}
                        </p>

                    </section>
                )}

            </div>
        </>
    );
};


export default DirectorMovieRolePopover;